import Cookies from "js-cookie";
import { CheckoutPlan, decorateUrlWithUTMParams, Web46DiscountPercent } from "../LandingPage";
import { getInternalCoreEndpoint } from "../utils";
import { sha256 } from "js-sha256";
import { User } from "../types";
import { LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY } from "./useAnswerTracker";
import { Web29AnchorCheckoutMode } from "../web29Offer";
import { isWelcomeKitPlan, WELCOME_KIT_OFFER_ID } from "../features/welcomeKit/welcomeKit";
import { getProteinTargetG } from "../quiz/protein";
import { getCheckoutReferralCode } from "../referral";
import { getStoredShareId } from "../shareLink";
import { isAppCheckoutEntry, readAppCheckoutDiscountId, readAppCheckoutReturnParams } from "../appCheckoutEntry";
import i18n from "../i18n";

export type Web46DiscountArm = "personalization" | "discount" | "discount30";

const WEB46_COUPONS: Record<Web46DiscountArm, { annual: string; monthly: string }> = {
  personalization: {
    annual: "WEB46_MATCH40_ANNUAL",
    monthly: "WEB46_MATCH40_MONTHLY",
  },
  discount: {
    annual: "WEB46_SPECIAL40_ANNUAL",
    monthly: "WEB46_SPECIAL40_MONTHLY",
  },
  // 12.5% off control's price = 30% off the anchor.
  discount30: {
    annual: "WEB46_SPECIAL30_ANNUAL",
    monthly: "WEB46_SPECIAL30_MONTHLY",
  },
};

export const getWeb46DiscountPercent = (arm: Web46DiscountArm): Web46DiscountPercent =>
  arm === "discount30" ? 30 : 40;

// The app's new-user offer coupons (bws-app remote config
// stripeNewUserOfferDiscountIdAnnual / stripeNewUserOfferDiscountIdMonthly) and
// the web46 coupons each take the arm whose anchor display they advertise:
// 12.5% off control's price is 30% off the anchor, 25% is 40%. Resolving the
// arm from the coupon gives the app's checkout the same display as the web
// ($13.99/mo or $26.24/mo, "30% off your first year"), not the coupon's raw
// percent.
const APP_OFFER_COUPON_ARMS: Record<string, Web46DiscountArm> = {
  BF2026_NEWUSER_EARLY_ANNUAL: "discount30",
  BF2026_NEWUSER_EARLY_MONTHLY: "discount30",
  BF2026_NEWUSER_ANNUAL: "discount",
  BF2026_NEWUSER_MONTHLY: "discount",
};

export function getWeb46DiscountArmForAnnualCoupon(
  couponId: string | undefined
): Web46DiscountArm | undefined {
  if (!couponId) return undefined;
  return (
    APP_OFFER_COUPON_ARMS[couponId] ??
    (["discount", "discount30"] as const).find(
      (arm) =>
        WEB46_COUPONS[arm].annual === couponId ||
        WEB46_COUPONS[arm].monthly === couponId
    )
  );
}

// Control's list price ($189/yr, $29.99/mo) is itself 20% off the anchor the
// funnel advertises against; the after-trial line names it on the renewal so
// the first-year percent is not read as a discount from the renewal price.
export const CONTROL_PERCENT_OFF_ANCHOR = 20;

export function getWeb46CouponId(
  plan: CheckoutPlan | undefined,
  arm: Web46DiscountArm | undefined
): string | undefined {
  if (!arm || (plan?.id !== "committed" && plan?.id !== "serious")) return undefined;
  return plan.id === "committed"
    ? WEB46_COUPONS[arm].annual
    : WEB46_COUPONS[arm].monthly;
}

// Shared by initial checkout and promo-code session recreation. These are
// independent metadata fields: a welcome kit must not replace the trial mode.
export function getCheckoutOfferMetadata(
  plan: CheckoutPlan | undefined,
  isNoTrial: boolean,
  urgencyAnchorMode: Web29AnchorCheckoutMode | null,
  // web40 variation only: the annual welcome kit is part of this checkout.
  welcomeKit = false
): Record<string, string> {
  const metadata: Record<string, string> = {};
  if (isNoTrial) {
    metadata.pricing_variant = 'no_trial';
  } else if (urgencyAnchorMode) {
    metadata.pricing_variant = urgencyAnchorMode === 'offer'
      ? 'urgency_anchor'
      : `urgency_anchor_${urgencyAnchorMode}`;
  }
  if (welcomeKit && isWelcomeKitPlan(plan)) {
    metadata.offer_id = WELCOME_KIT_OFFER_ID;
  }
  return metadata;
}

export function createGetClientSecretPromise(
  plan?: CheckoutPlan,
  eventID?: string,
  user?: User,
  isWeb17Variation?: boolean,
  // No trial (web35 variation, or ?trial=false): pay today on the plan's own
  // price, no promotion code — the 20% is a display anchor. useCheckoutPlan
  // has already picked control's price (offer live/revived) or the regular
  // ÷0.8 price (expired) via urgencyAnchorMode.
  isNoTrial = false,
  // Site baseline offer state: "offer" / "email" ride control's price,
  // "expired" the regular (÷0.8) price — no coupon in any mode; the value only
  // sets the Stripe pricing_variant tag.
  urgencyAnchorMode: Web29AnchorCheckoutMode | null = null,
  // web40_welcome_kit variation: tag the session with the kit offer.
  welcomeKit = false,
  // web46_match_offer: 25% off control's price produces the advertised 40%
  // anchor discount (12.5% → 30% for discount30). The caller only supplies an
  // arm while the timed offer is
  // active and trial checkout is allowed.
  web46DiscountArm?: Web46DiscountArm
) {
  // The iOS long pre-paywall entry runs this page in the phone's browser. Using
  // the "webview" checkout mode with the app's realtime connection id makes the
  // backend return the in-app post-purchase page ("open the app" + realtime
  // setup event) instead of the web post-purchase page.
  const appEntry = readAppCheckoutReturnParams(window.location.search);
  const mode = appEntry ? appEntry.mode : "web";
  const subscriptionPriceId = plan?.priceId ?? "";
  const unlockProductId = plan?.unlockProductId ?? "";
  const trialProductId = plan?.trialProductId ?? "";

  const amplitudeId = Cookies.get("bws_amplitude_device_id");
  const sessionIdentitySessionId = localStorage.getItem(
      LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY
  );

  let urlObj: URL;
  const web17_3DiscountId = plan?.experiments?.web17_3?.discount;
  // The app entry keeps its own offer coupon on the session (its Stripe name
  // is the offer name the customer sees); its web46 arm only sets the display.
  const discountId =
    readAppCheckoutDiscountId(window.location.search, plan) ??
    getWeb46CouponId(plan, web46DiscountArm);
  if (isNoTrial) {
    // create-subscription (no trial product → charged today) on the plan's
    // price with NO promotion code. Tagged pricing_variant=no_trial: the
    // backend's HubSpot workflow fork and the Meta "MetaStartSubscription"
    // event key on that exact value; offer metadata preserves that tag.
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-subscription/${subscriptionPriceId}/${unlockProductId}`
    );
  } else if (isWeb17Variation && web17_3DiscountId) {
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-discount/${subscriptionPriceId}/${unlockProductId}/${trialProductId}/${web17_3DiscountId}`
    );
  } else if (discountId) {
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-discount/${subscriptionPriceId}/${unlockProductId}/${trialProductId}/${encodeURIComponent(discountId)}`
    );
  } else {
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-trial/${subscriptionPriceId}/${unlockProductId}/${trialProductId}`
    );
  }

  // Site baseline offer tag: the backend stamps Stripe metadata pricingVariant
  // on the session + subscription (checkout.controller.ts METADATA_MAPPING):
  // urgency_anchor (offer redeemed on the pricing page), urgency_anchor_email
  // (revived via email), urgency_anchor_expired (paid the regular price). Pure
  // metadata. Core must validate annual eligibility and persist offer_id on
  // the session + subscription before HubSpot can consume it. A browser signal
  // never grants fulfillment eligibility.
  for (const [key, value] of Object.entries(getCheckoutOfferMetadata(plan, isNoTrial, urgencyAnchorMode, welcomeKit))) {
    urlObj.searchParams.set(key, value);
  }

  // web42: the protein target the quiz showed; Core stores it on the session
  // (metadata proteinTargetG) for the post-purchase protein offer.
  const proteinTargetG = getProteinTargetG(user);
  if (proteinTargetG) urlObj.searchParams.set("protein_target_g", String(proteinTargetG));

  urlObj.searchParams.set("ui_mode", "custom");
  if (appEntry) {
    urlObj.searchParams.set("connectionId", appEntry.connectionId);
    if (appEntry.attemptId) {
      urlObj.searchParams.set("attemptId", appEntry.attemptId);
    }
  }
  // Spanish app checkouts ask core for the Spanish in-app completion page.
  // English sends no lang, so its request is unchanged.
  if (appEntry && i18n.language === "es") {
    urlObj.searchParams.set("lang", "es");
  }
  urlObj.searchParams.set("tos_shown", "false");

  const emailToUse = (user?.email || "").trim();
  if (emailToUse) {
    urlObj.searchParams.set("email", sha256(emailToUse)); // Hashed for tracking
    urlObj.searchParams.set("prefillEmail", emailToUse); // Actual email for customer_email
  }

  if (user?.name) {
    urlObj.searchParams.set("name", user.name);
  }

  if (amplitudeId) {
    urlObj.searchParams.set("amplitudeId", amplitudeId);
  }

  if (eventID) {
      urlObj.searchParams.set("fb_event_id", eventID);
  }

  if (sessionIdentitySessionId) {
      urlObj.searchParams.set(
          "session_identity_session_id",
          sessionIdentitySessionId
      );
  }

  decorateUrlWithUTMParams(urlObj);

  return async () => {
    // The app's checkout entry gets control pricing: a referral stored by an
    // earlier web visit in this browser must not add its free months.
    const referralCode = isAppCheckoutEntry(window.location.search)
      ? undefined
      : getCheckoutReferralCode(plan, !isNoTrial && !(isWeb17Variation && web17_3DiscountId));
    if (referralCode) urlObj.searchParams.set("referral_code", referralCode);
    else urlObj.searchParams.delete("referral_code");
    // Attribution, not eligibility: every checkout route carries the share id.
    const shareId = getStoredShareId();
    if (shareId) urlObj.searchParams.set("share_id", shareId);
    else urlObj.searchParams.delete("share_id");
    const res = await fetch(urlObj.toString(), {
      method: "GET",
    });
    const data = await res.json();
    return data.client_secret;
  };
}
